import type { TWarehouse } from '@/shared/types';

import { today } from './auctionClock';

/**
 * Single-point configuration for the automated auction section (spec 006).
 *
 * Everything the business is expected to tune after seeing real usage lives here
 * rather than being scattered across components (FR-028, FR-030).
 */

/**
 * Auction sources PetrolBoys imports from. The provider exposes 16 sources
 * including Finland, UK, UAE and Brazil; only USA and Canada are in scope
 * (FR-003).
 *
 * Measured 2026-09-02, these four accounted for all 154,229 active lots, so the
 * filter is currently a no-op — but it is still sent explicitly on every request,
 * because non-US/CA inventory appearing later would otherwise leak in silently.
 */
export const AUCTION_SOURCES = ['COPART', 'COPART CANADA', 'IAAI', 'IAAI CANADA'] as const;

export type TAuctionSource = (typeof AUCTION_SOURCES)[number];

/* -------------------------------------------------------------------------- */
/* Provider selection (spec 007)                                              */
/* -------------------------------------------------------------------------- */

/** Polish labels for the source filter — the raw values are English trade names. */
export const AUCTION_SOURCE_LABELS: Record<TAuctionSource, string> = {
  COPART: 'Copart (USA)',
  'COPART CANADA': 'Copart (Kanada)',
  IAAI: 'IAAI (USA)',
  'IAAI CANADA': 'IAAI (Kanada)',
};

/**
 * Maps a provider source onto the warehouse value carried into enquiry emails.
 * The Canada distinction is preserved deliberately — it changes shipping origin
 * and therefore the quoted cost (FR-018).
 */
export const AUCTION_SOURCE_WAREHOUSE: Record<TAuctionSource, TWarehouse> = {
  COPART: 'Copart',
  'COPART CANADA': 'Copart Kanada',
  IAAI: 'IAAI',
  'IAAI CANADA': 'IAAI Kanada',
};

/* -------------------------------------------------------------------------- */
/* Market × house — how the source filter is presented                        */
/* -------------------------------------------------------------------------- */

/**
 * The four sources are really two independent choices: which country the car is
 * in, and which auction house is selling it. Splitting them that way is not
 * cosmetic — the country determines the CURRENCY every amount on the page is
 * quoted in, and a price filter offered without naming its currency silently
 * compares 20,000 CAD against 20,000 USD (a ~27% difference) as if they were the
 * same number.
 *
 * Presenting "Rynek" as its own control means the price fields can always state
 * the currency they are filtering in, or say plainly that two are in play.
 */
export const AUCTION_MARKETS = ['usa', 'kanada'] as const;
export type TAuctionMarket = (typeof AUCTION_MARKETS)[number];

export const isAuctionMarket = (value: string): value is TAuctionMarket =>
  AUCTION_MARKETS.includes(value as TAuctionMarket);

export const AUCTION_MARKET_LABELS: Record<TAuctionMarket, string> = {
  usa: 'USA',
  kanada: 'Kanada',
};

export const AUCTION_MARKET_CURRENCY: Record<TAuctionMarket, 'USD' | 'CAD'> = {
  usa: 'USD',
  kanada: 'CAD',
};

export const AUCTION_HOUSES = ['copart', 'iaai'] as const;
export type TAuctionHouse = (typeof AUCTION_HOUSES)[number];

export const isAuctionHouse = (value: string): value is TAuctionHouse =>
  AUCTION_HOUSES.includes(value as TAuctionHouse);

export const AUCTION_HOUSE_LABELS: Record<TAuctionHouse, string> = {
  copart: 'Copart',
  iaai: 'IAAI',
};

/**
 * The currency every amount in a result set is quoted in, or `null` when both
 * markets are in play and there is therefore no single answer. Callers must
 * disclose the `null` case rather than picking a default — see the note above.
 */
export const resolveMarketCurrency = (markets: readonly TAuctionMarket[]): 'USD' | 'CAD' | null =>
  markets.length === 1 ? AUCTION_MARKET_CURRENCY[markets[0]] : null;

/* -------------------------------------------------------------------------- */
/* Price basis                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Which amount the price range filters against.
 *
 * All three provider parameters were verified to filter for real — rows were
 * checked, not just the total, because an accepted-but-ignored parameter returns
 * HTTP 200 with unfiltered data (research.md R3):
 *  - `current_bid_*`      5,000–6,000    → 1,555 lots
 *  - `buy_now_price_*`    5,000–6,000    → 2,650 lots, every sampled price in band
 *  - `estimate_retail_*`  20,000–21,000  → 2,725 lots, every sampled value in band
 *
 * One range with a basis selector beats three separate range pairs: the visitor
 * makes a single decision about which number they care about, and the labels stay
 * unambiguous.
 */
export const PRICE_BASIS_OPTIONS = [
  {
    value: 'oferta',
    label: 'Aktualna oferta',
    from: 'current_bid_from',
    to: 'current_bid_to',
  },
  {
    value: 'kupTeraz',
    label: 'Cena „Kup teraz”',
    from: 'buy_now_price_from',
    to: 'buy_now_price_to',
  },
  {
    value: 'wartosc',
    label: 'Wartość rynkowa (szac.)',
    from: 'estimate_retail_from',
    to: 'estimate_retail_to',
  },
] as const;

export type TPriceBasis = (typeof PRICE_BASIS_OPTIONS)[number]['value'];

export const DEFAULT_PRICE_BASIS: TPriceBasis = 'oferta';

export const isPriceBasis = (value: string): value is TPriceBasis =>
  PRICE_BASIS_OPTIONS.some(option => option.value === value);

export const getPriceBasisOption = (value: TPriceBasis) =>
  PRICE_BASIS_OPTIONS.find(option => option.value === value) ?? PRICE_BASIS_OPTIONS[0];

/* -------------------------------------------------------------------------- */
/* Model years                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Year bounds, shared by the validator and the dropdown so the UI can never
 * offer a value the parser would silently discard.
 *
 * The floor is genuinely 1900 — measured, not guessed. Inventory thins out but
 * never stops: 1900–1949 holds 26 lots, 1950–1969 holds 99, 1970s 191, 1980s
 * 494. Cutting the list at 1990 would have made 810 real cars unreachable,
 * including the pre-war and muscle-era lots people specifically hunt for.
 */
export const MIN_AUCTION_YEAR = 1900;

/** Validation ceiling. Deliberately looser than the dropdown — see below. */
export const getMaxAuctionYear = (): number => today().getFullYear() + 2;

/**
 * Dropdown options, newest first — 55.7% of stock is 2010s and 27.7% is 2020s,
 * so recent years must be the ones you land on.
 *
 * Stops one year ahead of today rather than two: model years run a year ahead of
 * the calendar, so `currentYear + 1` is the last one with any plausible
 * inventory. Validation still accepts `+2`, making the accepted range a superset
 * of the offered one — a hand-edited URL keeps working, while the list carries no
 * option that is guaranteed to return nothing.
 */
export const getAuctionYearOptions = (): number[] => {
  const newest = today().getFullYear() + 1;
  const years: number[] = [];

  for (let year = newest; year >= MIN_AUCTION_YEAR; year--) years.push(year);

  return years;
};

/* -------------------------------------------------------------------------- */
/* Date windows                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Dates are offered as fixed windows rather than free date pickers, for three
 * measured reasons:
 *
 *  1. A lone `auction_date_from` does NOT mean "from then on" — it silently
 *     collapses to that single day (`from=today` returned 40,616, identical to
 *     `[today..today]`). A lone `auction_date_to` breaks the response outright.
 *     Presets always send a paired, explicit range.
 *  2. Auctions do not run at weekends: `[2026-09-05..2026-09-05]`, a Saturday,
 *     returns 0 lots. A free picker would hand visitors guaranteed-empty days.
 *  3. Wide ranges are pointless — `[today..today+30]` returns 154,826 of 154,965
 *     lots, because nearly all inventory sells within a month.
 *
 * Storing the preset (`?termin=3`) rather than resolved dates also keeps the ISR
 * cache key stable across a day and stops shared links from freezing to an
 * absolute date that silently ages into the past.
 */
export const AUCTION_DATE_WINDOWS = [
  { value: '1', label: 'Dziś', days: 1 },
  { value: '3', label: 'Najbliższe 3 dni', days: 3 },
  { value: '7', label: 'Najbliższe 7 dni', days: 7 },
] as const;

/**
 * Upper bound used when no "Termin aukcji" preset is chosen.
 *
 * Its real job is to make the LOWER bound expressible: the provider rejects a
 * lone `auction_date_from`, so pinning the floor at today needs some `_to`. A
 * year is comfortably past the end of the data — `[today..+365]` and
 * `[today..+730]` both return 155,251 — so it excludes nothing real.
 */
export const AUCTION_DATE_HORIZON_DAYS = 365;

export type TDateWindow = (typeof AUCTION_DATE_WINDOWS)[number]['value'];

export const isDateWindow = (value: string): value is TDateWindow =>
  AUCTION_DATE_WINDOWS.some(option => option.value === value);

export const getAuctionDateWindow = (value: TDateWindow) =>
  AUCTION_DATE_WINDOWS.find(option => option.value === value) ?? AUCTION_DATE_WINDOWS[0];

/**
 * `YYYY-MM-DD`, the format the provider's date filters accept, offset by whole
 * days. Resolved in Europe/Warsaw because the labels ("Dziś") are read by Polish
 * visitors — `en-CA` is used purely because it formats as ISO.
 */
const WARSAW_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' });

export const auctionDay = (offsetDays: number): string => {
  const date = today();

  date.setDate(date.getDate() + offsetDays);

  return WARSAW_DATE.format(date);
};

/**
 * Vehicle makes that actually carry active inventory, most common first.
 *
 * The provider's make catalogue is a historical dump of 2,849 entries dominated
 * by equipment and trailer manufacturers — "4 W", "58TH STREET CUSTOMS",
 * "AA WELDING", "ADVANCE MECHANICAL SERVICE". Sorted alphabetically, those are
 * literally the first thing a visitor sees, while none of them has a single lot
 * to buy.
 *
 * Sampling 900 live lots produced just 79 distinct makes, and the order below is
 * that measured frequency (Toyota 101, Chevrolet 95, Ford 89 …) extended with
 * marques PetrolBoys customers ask for. It drives the default dropdown and ranks
 * typed matches, so real brands always beat catalogue noise.
 *
 * This is a display-ordering hint, not a whitelist: anything in the catalogue is
 * still findable by typing, so a rare marque never becomes unfilterable.
 */
export const PRIORITY_MAKES = [
  'TOYOTA', 'CHEVROLET', 'FORD', 'NISSAN', 'HONDA', 'HYUNDAI', 'KIA', 'JEEP',
  'MERCEDES-BENZ', 'DODGE', 'MAZDA', 'BMW', 'GMC', 'SUBARU', 'LEXUS', 'VOLKSWAGEN',
  'RAM', 'CHRYSLER', 'LAND ROVER', 'ACURA', 'MITSUBISHI', 'CADILLAC', 'INFINITI',
  'TESLA', 'AUDI', 'VOLVO', 'BUICK', 'PORSCHE', 'SUZUKI', 'LINCOLN', 'MINI',
  'JAGUAR', 'GENESIS', 'FIAT', 'ALFA ROMEO', 'MASERATI', 'BENTLEY', 'FERRARI',
  'LAMBORGHINI', 'ROLLS-ROYCE', 'ASTON MARTIN', 'RIVIAN', 'LUCID', 'POLESTAR',
  'HUMMER', 'SATURN', 'PONTIAC', 'MERCURY', 'SCION', 'SMART',
];

const PRIORITY_MAKE_RANK = new Map(PRIORITY_MAKES.map((make, index) => [make, index]));

export const getMakeRank = (make: string): number =>
  PRIORITY_MAKE_RANK.get(make.toUpperCase()) ?? Number.MAX_SAFE_INTEGER;

/**
 * Vehicle types PetrolBoys does not import. `vehicle_type` is free text from the
 * auction house, so matching is case-insensitive and substring-based (FR-004).
 */
export const EXCLUDED_VEHICLE_TYPES = [
  'trailer',
  'industrial',
  'recreational',
  'atv',
  'jet ski',
  'boat',
  'aircraft',
];

/**
 * Is this vehicle type one we do not import? (FR-006)
 *
 * Both normalisers had their own copy of this test. Consolidating it means the
 * two providers cannot drift apart on what counts as a car.
 *
 * The match stays a SUBSTRING one, deliberately. A word-boundary rule was tried
 * and is worse: it stops excluding "Powerboat", which is unambiguously a boat.
 * The theoretical cost of substring matching is `atv`, short enough to appear
 * inside an unrelated word — but no real type string does, and the concrete
 * regression on the boats outweighs the hypothetical one on ATVs.
 *
 * Non-string input counts as NOT excluded. This payload has twice turned out to
 * hold an object where a string was declared, and of the two ways to be wrong,
 * dropping every lot is much the worse.
 */
export const isExcludedVehicleType = (type: unknown): boolean => {
  if (typeof type !== 'string') return false;

  const lowered = type.toLowerCase();

  return EXCLUDED_VEHICLE_TYPES.some(excluded => lowered.includes(excluded));
};

/** Provider hard cap is 50 — `per_page=100` returns 422 despite what the vendor documents. */
export const LOTS_PER_PAGE = 24;

/**
 * apibara caps `per_page` BY PLAN TIER — 20 on Test, 30-40 higher — below the 24
 * the grid is built for. Asking for more than the cap risks a rejected request,
 * so the page size is the smaller of the two (FR-017).
 *
 * This is a constant rather than something read from the plan, because it has to
 * be known before any request is built. That makes it a number somebody has to
 * remember to raise after an upgrade — which nobody would, since nothing breaks:
 * the pages just stay smaller than they need to be, forever, invisibly.
 *
 * So `checkApibaraAllowance` compares it against the `per_page_limit` the plan
 * actually reports and says so. The hourly `/usage` poll already fetches that
 * value; noticing it costs nothing.
 */
export const APIBARA_PER_PAGE_CAP = 20;

/* -------------------------------------------------------------------------- */
/* Freshness (spec 016)                                                       */
/* -------------------------------------------------------------------------- */

/**
 * How long a provider answer is served before it is asked for again.
 *
 * These replaced three `revalidate` constants (an hour for everything live, six
 * hours for sold, a day for dictionaries) when the answer cache moved out of
 * the framework and into `providerCache.ts`. One number per class of answer,
 * because the classes change at very different speeds:
 *
 *  - `listing`      3 h  — the live listing, make and model pages, the board's
 *                          upcoming lane, the comparable strip, the sitemap
 *                          probe (which asks the model page's own query).
 *                          Spec 016 relaxed this from an hour deliberately; the
 *                          lot a visitor clicks through to is still fresh.
 *  - `listingNow`  30 m  — `?stan=na-zywo`. "Running right now" is read from
 *                          the provider's `state`, and three hours would show
 *                          ended lanes as live.
 *  - `lot`          1 h  — one lot by number, and `?szukaj=` lookups.
 *  - `lotFinished` 24 h  — a lot whose auction has finished. Decided AFTER the
 *                          answer arrives, so the same query is re-stored under
 *                          the longer window once the sale has run.
 *  - `history`     24 h  — `q=<VIN>` record searches and `/history` lanes. The
 *                          past does not change; the current record's own lanes
 *                          may be up to a day behind on the timeline (FR-012).
 *  - `sold`        24 h  — `?stan=sprzedane` and the board's sold lane. A sale
 *                          that ran does not change, and the query's date
 *                          window rolls daily anyway.
 *  - `dictionary`  24 h  — facilities and the make/model catalogue.
 *
 * Every entry is kept for its window plus `STALE_GRACE_SECONDS`; in between it
 * is refreshed, and served marked stale only if the refresh fails.
 */
export type TFreshnessClass =
  | 'listing'
  | 'listingNow'
  | 'lot'
  | 'lotFinished'
  | 'history'
  | 'sold'
  | 'dictionary';

export const FRESHNESS_SECONDS: Record<TFreshnessClass, number> = {
  listing: 10_800,
  listingNow: 1_800,
  lot: 3_600,
  lotFinished: 86_400,
  history: 86_400,
  sold: 86_400,
  dictionary: 86_400,
};

/** Three days: covers a long-weekend outage; more would only cost memory. */
export const STALE_GRACE_SECONDS = 259_200;

/**
 * An expired answer is served AT ONCE and refreshed behind the visitor (spec
 * 016, stale-while-revalidate). Up to this multiple of its window it is served
 * quietly; older than that it is served marked stale, so the page says the
 * figures may have moved. A three-hour listing is quiet until it is six hours
 * old, an hourly lot until two.
 */
export const STALE_QUIET_MULTIPLIER = 2;

/**
 * Search-engine indexing for the /aukcje LISTING pages — root, make and model
 * (spec 012).
 *
 * Held at `false` from spec 006 until spec 012 on the grounds that thousands of
 * thin, short-lived, near-duplicate pages would risk the curated
 * /propozycje-licytacji rankings (FR-028). Spec 012 measured those rankings
 * before flipping it: over eight weeks the curated section earned **90
 * impressions and zero clicks**, and two of its three queries are the brand name
 * itself, which no auction listing can plausibly take. The risk was real to
 * reason about and turned out to be about one impression wide.
 *
 * Flipping this is HALF of going live. The other half is `MIN_LOTS_FOR_INDEXING`
 * below: constitution Principle III's thin-content guard applies in full the
 * moment these pages are indexable, so a page that renders nothing must say so
 * itself. The pages already carry unique titles and descriptions, one relevant
 * h1, canonicals, OpenGraph and breadcrumb JSON-LD, so no content rewrite is
 * needed — only the bar.
 *
 * `/aukcje` stays out of the committed static sitemap. It is advertised by a
 * separate, always-fresh sitemap instead, because inventory changes daily and a
 * build-time file would be wrong most of the time.
 */
export const AUCTION_PAGES_INDEXABLE = true;

/**
 * Lot detail pages are NEVER indexable, whatever the flag above says (FR-003).
 *
 * They are the richest pages in the section — photos, mileage, damage, sale
 * result, breadcrumb JSON-LD — and the only ones that DIE. An auction ends
 * within days, the lot leaves the provider, and the page then answers HTTP 200
 * with an unavailable state: a soft 404, at a scale of tens of thousands of URLs
 * (one model alone holds over 700 live lots).
 *
 * They keep `follow`, and that half is load-bearing rather than leftover: those
 * pages link up to their model and make listings, and the crawler should keep
 * walking them. Only `index` is withheld.
 *
 * A constant rather than a literal `false` so the reason lives with the value
 * and a future reader flipping the listings flag does not assume this follows.
 */
export const AUCTION_LOT_PAGES_INDEXABLE = false;

/**
 * The content bar: lots a listing page must RENDER to deserve indexing (FR-004).
 *
 * One car is not a listing. A page whose h1 promises "Audi A4 z USA i Kanady"
 * and shows one car fails the promise it makes, and reads as thin under
 * constitution Principle III. Three is where the page answers the question it
 * asks.
 *
 * ONE DEFINITION, TWO READERS — the page's `robots` decision and the sitemap's
 * membership test both read this. Two copies would drift, and the drift is
 * invisible: a page would go `noindex` while still being advertised, which is
 * exactly what a sitemap must never do.
 *
 * Counted on RENDERED lots, not catalogue entries. A model with twenty catalogue
 * spellings and two live cars shows two cars, and the visitor does not care how
 * many ways the vendor spells it.
 */
export const MIN_LOTS_FOR_INDEXING = 3;

/**
 * Does a listing page hold enough to be offered to search engines?
 *
 * Costs NOTHING: a listing page has already fetched its lots by the time it
 * emits metadata, so this is a length check on data in hand rather than a
 * request. That is what makes the page — not the sitemap — the authority on its
 * own indexability. The sitemap is a claim refreshed every few hours; the page
 * is always current, and can only ever be MORE restrictive, never less.
 */
export type TNoIndex = { index: false; follow: true };

/**
 * Withhold indexing but keep the crawl. Every page in this section that is not
 * indexable still links to ones that are, so `follow` is never dropped.
 */
export const NO_INDEX_FOLLOW: TNoIndex = { index: false, follow: true };

export const isListingIndexable = (renderedLots: number): boolean =>
  AUCTION_PAGES_INDEXABLE && renderedLots >= MIN_LOTS_FOR_INDEXING;

/** `robots` metadata for a listing page, from what it is about to render. */
export const listingRobots = (renderedLots: number): TNoIndex | undefined =>
  isListingIndexable(renderedLots) ? undefined : NO_INDEX_FOLLOW;

/** `robots` for a lot page. Always `noindex, follow` — see above. */
export const lotRobots = (): TNoIndex | undefined =>
  AUCTION_LOT_PAGES_INDEXABLE ? undefined : NO_INDEX_FOLLOW;

/* -------------------------------------------------------------------------- */
/* apibara filters (spec 007)                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The filters this provider was MEASURED to honour, with the vocabularies it
 * publishes at `/vehicles/filters`.
 *
 * Everything here passed Gate 1 on 2026-09-03: for each one, every returned row
 * satisfied the filter AND the distribution moved against an unfiltered baseline.
 * A parameter that merely returns HTTP 200 proves nothing — that is exactly how
 * the previous provider shipped nine dead controls, and the defect this spec
 * exists to fix (FR-002, FR-003).
 *
 * Values are NOT invented. Two of these failed on the first attempt purely
 * because the values were guessed: `has_key=1` returned 20/20 *false*, and
 * `fuel_type[]=GAS` returned the baseline untouched. The provider's own
 * vocabulary is authoritative.
 *
 * `drive_type` WAS withdrawn here and has been restored (spec 008). It was tested
 * on the slice `make=FORD&platform=copart`, where `AWD` returns zero rows — but
 * that slice genuinely has no AWD cars: Copart labels those Fords
 * "4x4 w/Front Whl Drv" rather than AWD. Against the unfiltered inventory,
 * `FWD` and `RWD` each return 20 of 20 matching rows.
 *
 * A third way to reach a false verdict, after an invented value and a
 * twenty-row sample: **a slice too narrow to contain the thing being tested.**
 * An empty result proves nothing unless the unfiltered slice is known to hold
 * matches.
 */

export const APIBARA_RUN_CONDITIONS = [
  { value: 'RUNS AND DRIVES', label: 'Odpala i jeździ' },
  { value: 'ENGINE START PROGRAM', label: 'Silnik odpala' },
  { value: 'STATIONARY / NO INFORMATION', label: 'Nie odpala / brak informacji' },
] as const;

export type TRunCondition = (typeof APIBARA_RUN_CONDITIONS)[number]['value'];

export const isRunCondition = (value: string): value is TRunCondition =>
  APIBARA_RUN_CONDITIONS.some(option => option.value === value);

/** `With` / `No` / `All` — not booleans, and not `1`/`0`. */
export const APIBARA_KEYS_OPTIONS = [
  { value: 'With', label: 'Z kluczykami' },
  { value: 'No', label: 'Bez kluczyków' },
] as const;

export type TKeysOption = (typeof APIBARA_KEYS_OPTIONS)[number]['value'];

export const isKeysOption = (value: string): value is TKeysOption =>
  APIBARA_KEYS_OPTIONS.some(option => option.value === value);

export const APIBARA_TRANSMISSIONS = [
  { value: 'Automatic', label: 'Automatyczna' },
  { value: 'Manual', label: 'Manualna' },
] as const;

export const APIBARA_FUEL_TYPES = [
  { value: 'Gasoline', label: 'Benzyna' },
  { value: 'Diesel', label: 'Diesel' },
  { value: 'Hybrid', label: 'Hybryda' },
  { value: 'Electric', label: 'Elektryczny' },
  { value: 'Flexible', label: 'Flex-fuel' },
] as const;

export const APIBARA_COLORS = [
  { value: 'Black', label: 'Czarny' },
  { value: 'White', label: 'Biały' },
  { value: 'Silver', label: 'Srebrny' },
  { value: 'Gray', label: 'Szary' },
  { value: 'Blue', label: 'Niebieski' },
  { value: 'Red', label: 'Czerwony' },
  { value: 'Green', label: 'Zielony' },
  { value: 'Yellow', label: 'Żółty' },
] as const;

/**
 * Drive type. Values are the provider's own (FWD/RWD/AWD), taken from its demo
 * rather than guessed — the mistake that produced three false negatives here.
 *
 * The payload spells the answers out in full ("Front Wheel Drive") and uses
 * "4x4 w/..." for some four-wheel-drive cars, so the filter is narrower than the
 * label suggests on some inventory. That is the provider's shape, not ours.
 */
export const APIBARA_DRIVE_TYPES = [
  { value: 'FWD', label: 'Przedni' },
  { value: 'RWD', label: 'Tylny' },
  { value: 'AWD', label: 'Na cztery koła' },
] as const;

export const APIBARA_DAMAGE_TYPES = [
  { value: 'Mechanical', label: 'Mechaniczne' },
  { value: 'Hail', label: 'Grad' },
  { value: 'Fire', label: 'Pożar' },
  { value: 'Water', label: 'Zalanie' },
  { value: 'Theft', label: 'Kradzież' },
  { value: 'Rollover', label: 'Dachowanie' },
  { value: 'Vandalized', label: 'Wandalizm' },
  { value: 'Repossession', label: 'Przejęcie' },
] as const;

const inList = (options: readonly { value: string }[], value: string) =>
  options.some(option => option.value === value);

export const isApibaraTransmission = (v: string) => inList(APIBARA_TRANSMISSIONS, v);
export const isApibaraFuelType = (v: string) => inList(APIBARA_FUEL_TYPES, v);
export const isApibaraColor = (v: string) => inList(APIBARA_COLORS, v);
export const isApibaraDamage = (v: string) => inList(APIBARA_DAMAGE_TYPES, v);
export const isApibaraDriveType = (v: string) => inList(APIBARA_DRIVE_TYPES, v);

/**
 * Price bases available on apibara — ONE, not three.
 *
 * `price_min`/`price_max` were verified to filter, and they filter on the
 * CURRENT BID. Nothing was found that filters on the buy-now price, so offering
 * "Cena kup teraz" would quietly apply the range to a different number than the
 * label promises — worse than a control that does nothing, because the results
 * look plausible (FR-002, Principle I).
 *
 * Estimated market value is gone for a different reason: `pricing.estimated_cost`
 * is a repair-cost range, a different quantity, and substituting it would
 * misdescribe what the buyer is filtering on (FR-011).
 *
 * Restore `kupTeraz` here the moment a buy-now price filter is verified.
 */
export const VERIFIED_PRICE_BASES = ['oferta'] as const;

export const isVerifiedPriceBasis = (value: TPriceBasis): boolean =>
  (VERIFIED_PRICE_BASES as readonly string[]).includes(value);

/** Odometer is reported in MILES by both auction houses; the UI converts for display. */
export const MAX_ODOMETER_MILES = 500_000;

/**
 * The sold mode's window and probe (spec 015; widened to six months by spec
 * 021 — see `SOLD_WINDOW_START_DAYS_AGO` below).
 *
 * A past window returns NOTHING without the free-text search, and on the day
 * an auction closes every result is "Sold on Approval" — it settles to "Sold"
 * a couple of days later. Without a make the probe letter is the closest thing
 * this API has to "everything"; WITH a make the same letter is a model prefix,
 * which is why make pages get no sold mode.
 *
 * TWO BOUNDS, AND THEY ARE NOT THE SAME DAY. Measured on 2026-09-15 (q=C,
 * one auction day per request, six pages of 20 each, twice during the day):
 *
 *    0d    0 of 120 confirmed — no status yet at all
 *   -1d   31 of 120 confirmed (71 "Sold on Approval", 18 no status)
 *   -2d  120 of 120      -3d 120 of 120      -5d 120 of 120
 *
 * A day is FINAL two days on: everything that will ever say "Sold" does.
 * That is `SOLD_SETTLED_DAYS_AGO`, and the sweep only marks a day done
 * (`sold_days.complete`) once it is that old — nothing re-reads a day marked
 * done, so a day closed early would keep its approvals for ever.
 *
 * But a quarter of YESTERDAY is already confirmed, and those are real sales a
 * visitor wants to see, so the listing shows from one day back
 * (`SOLD_WINDOW_END_DAYS_AGO`) and the sweep re-reads the unsettled days on a
 * cooldown as the rest of them settle. Spec 014 had this at five days, from a
 * single probe of "today"; the listing opened on sales a working week old and
 * read as an archive (owner, 2026-09-15).
 *
 * Not zero: today's lanes have no result at all while they are still running,
 * and what has run today is the `now` mode's job. "Sold on Approval" is never
 * a sale, anywhere — only `Sold` is.
 */
/**
 * THE LISTED WINDOW — 180 days since spec 021, and read by the STORE ALONE.
 *
 * It was 30, and sales vanished from "Sprzedane" a month after they happened:
 * a month is too thin a sample to judge a price on, and the same filters run
 * six weeks apart returned a different history (issue #70, owner 2026-09-18).
 * Measured the same day, the provider answers a make + model-prefix window at
 * 180 and 210 days back in about a second and runs dry between 210 and 240, so
 * six months is inside what can actually be held.
 *
 * THREE PLACES USED TO READ THIS ONE NUMBER AND TWO OF THEM BREAK AT 180:
 *
 *  - `src/inventory/queries.ts` — the store's sold predicate. This one, and the
 *    only one. It is what the visitor sees.
 *  - `toApibaraParams` — the direct-to-provider path (the rollback path, with
 *    `INVENTORY_SOURCE` unset). It searches the past with `q=C`, which times
 *    out (502 after 27 s) beyond about 35 days, so it reads
 *    `SOLD_PROVIDER_WINDOW_START_DAYS_AGO` below and keeps the short window.
 *  - `soldBackfillDays()` in `passes/sold.ts` — its cap USED to be
 *    `START − END`, so widening this would have sent the sweep at 179 days of
 *    those same timing-out queries, ten pages each. It now carries its own
 *    default; the past band is what fills six months.
 *
 * So: widen this number and nothing else, and the listing stays a month deep
 * while the rollback path and the sold sweep quietly fail. They move together.
 */
export const SOLD_WINDOW_START_DAYS_AGO = 180;
/**
 * What the direct-to-provider path may ask the past for: a month, no more.
 * `q=C` is the only way to search a past window at all (a bare make returns
 * nothing) and it is too heavy to answer beyond ~35 days.
 */
export const SOLD_PROVIDER_WINDOW_START_DAYS_AGO = 30;
export const SOLD_WINDOW_END_DAYS_AGO = 1;
/** A sold day stops changing at this age; before it, the sweep comes back. */
export const SOLD_SETTLED_DAYS_AGO = 2;
/** How long the sweep leaves an unsettled day alone before reading it again. */
export const SOLD_RESWEEP_HOURS = 4;
export const SOLD_PROBE_PREFIX = 'C';
